/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { TimeInterval } from '@opengeoweb/webmap';
import { isStacViewportBboxQueryable } from './useStacViewportBbox';
import type { STACItem } from '../../../geotiff-layer-api/api';
import { resolvePreferredStyleName as resolveStacPreferredStyleName } from '../../../geotiff-layer-api/utils/styleSelection';

export const STAC_ITEM_LOOKUP_WINDOW_MS = 60 * 60 * 1000;
export const STAC_ITEM_LOOKUP_WINDOW_INTERVAL_MULTIPLIER = 20;
// Tiled third-party collections can contain hundreds of spatial items per day.
// Only discover the selected interval; adjacent frames are loaded on demand.
export const STAC_GEOTIFF_FRAME_PRELOAD_RADIUS = 4;
export const STAC_TILED_ITEM_PAGE_LIMIT = 100;
export const STAC_TILED_OVERVIEW_ITEM_PAGE_LIMIT = 1000;

export interface STACFrameItem {
  item: STACItem;
  isSelected: boolean;
}

interface ResolveStacItemQueryStateProps {
  hasCollectionData: boolean;
  isTiledCollection: boolean;
  rasterViewportBbox?: number[];
  shouldUseSpatialFilter: boolean;
  viewportBbox?: number[];
}

interface StacItemQueryState {
  isItemQueryEnabled: boolean;
  isTiledOverview: boolean;
  itemQueryBbox?: number[];
}

const isGlobalViewportBbox = (bbox?: number[]): boolean =>
  Boolean(
    bbox?.length === 4 &&
    bbox.every(Number.isFinite) &&
    bbox[0] <= -179 &&
    bbox[1] <= -89 &&
    bbox[2] >= 179 &&
    bbox[3] >= 89,
  );

export const resolveStacItemQueryState = ({
  hasCollectionData,
  isTiledCollection,
  rasterViewportBbox,
  shouldUseSpatialFilter,
  viewportBbox,
}: ResolveStacItemQueryStateProps): StacItemQueryState => {
  const isTiledOverview =
    isTiledCollection && !rasterViewportBbox && Boolean(viewportBbox);
  const isOverviewViewportQueryable = isStacViewportBboxQueryable(viewportBbox);

  let itemQueryBbox = viewportBbox;
  if (isTiledCollection) {
    itemQueryBbox =
      isTiledOverview && !isOverviewViewportQueryable
        ? undefined
        : (rasterViewportBbox ?? viewportBbox);
  }

  const isItemQueryEnabled =
    hasCollectionData &&
    (!shouldUseSpatialFilter ||
      (isTiledOverview
        ? isStacViewportBboxQueryable(viewportBbox) ||
          isGlobalViewportBbox(viewportBbox)
        : isStacViewportBboxQueryable(itemQueryBbox)));

  return {
    isItemQueryEnabled,
    isTiledOverview,
    itemQueryBbox,
  };
};

export const getSingleStacRasterItem = (
  frameItems: STACFrameItem[],
  selectionBbox?: number[],
  preferredItemId?: string,
): STACFrameItem[] => {
  const preferredItem = frameItems.find(
    ({ item }) => item.id === preferredItemId,
  );
  if (preferredItem) {
    return [{ item: preferredItem.item, isSelected: true }];
  }

  if (frameItems.length <= 1) {
    return frameItems;
  }

  const selectedItems = frameItems.filter(({ isSelected }) => isSelected);
  if (selectionBbox?.length !== 4) {
    const candidates = selectedItems.length > 0 ? selectedItems : frameItems;
    const selectedCandidate = [...candidates].sort((first, second) =>
      first.item.id.localeCompare(second.item.id),
    )[0];
    return [{ item: selectedCandidate.item, isSelected: true }];
  }

  const viewportCenter: [number, number] = [
    (selectionBbox[0] + selectionBbox[2]) / 2,
    (selectionBbox[1] + selectionBbox[3]) / 2,
  ];

  const selectedItemsCoverViewportCenter = selectedItems.some(({ item }) => {
    const { bbox } = item;
    return (
      bbox?.length === 4 &&
      bbox[0] <= viewportCenter[0] &&
      bbox[1] <= viewportCenter[1] &&
      bbox[2] >= viewportCenter[0] &&
      bbox[3] >= viewportCenter[1]
    );
  });

  const candidates =
    selectedItems.length > 0 && selectedItemsCoverViewportCenter
      ? selectedItems
      : frameItems;

  const getItemRank = ({ item }: STACFrameItem): [number, number, string] => {
    const { bbox } = item;
    if (bbox?.length !== 4) {
      return [1, Number.POSITIVE_INFINITY, item.id];
    }
    const containsCenter =
      bbox[0] <= viewportCenter[0] &&
      bbox[1] <= viewportCenter[1] &&
      bbox[2] >= viewportCenter[0] &&
      bbox[3] >= viewportCenter[1];
    const itemCenter = [(bbox[0] + bbox[2]) / 2, (bbox[1] + bbox[3]) / 2];
    const centerDistance =
      (itemCenter[0] - viewportCenter[0]) ** 2 +
      (itemCenter[1] - viewportCenter[1]) ** 2;
    return [containsCenter ? 0 : 1, centerDistance, item.id];
  };
  const sortedCandidates = [...candidates].sort((first, second) => {
    const firstRank = getItemRank(first);
    const secondRank = getItemRank(second);
    return (
      firstRank[0] - secondRank[0] ||
      firstRank[1] - secondRank[1] ||
      firstRank[2].localeCompare(secondRank[2])
    );
  });

  return [{ item: sortedCandidates[0].item, isSelected: true }];
};

export type STACItemLookupWindow = [string | undefined, string | undefined];

interface STACItemWithTime {
  item: STACItem;
  index: number;
  time: number;
}

export const resolvePreferredStyleName = resolveStacPreferredStyleName;

export const getTimeIntervalInMilliseconds = (
  interval?: TimeInterval,
): number | undefined => {
  const supportedInterval =
    interval?.year === 0 && interval?.month === 0 ? interval : undefined;

  if (!supportedInterval) {
    return undefined;
  }

  const milliseconds =
    (((supportedInterval.day * 24 + supportedInterval.hour) * 60 +
      supportedInterval.minute) *
      60 +
      supportedInterval.second) *
    1000;

  return milliseconds > 0 ? milliseconds : undefined;
};

export const getStacItemLookupWindowForTimes = (
  timeValues: string[],
  windowMs = STAC_ITEM_LOOKUP_WINDOW_MS,
): STACItemLookupWindow => {
  const validTimes = timeValues
    .map((timeValue) => new Date(timeValue).getTime())
    .filter((timeValue) => Number.isFinite(timeValue));

  if (validTimes.length === 0) {
    return [undefined, undefined];
  }

  const halfWindow = windowMs / 2;
  return [
    new Date(Math.min(...validTimes) - halfWindow).toISOString(),
    new Date(Math.max(...validTimes) + halfWindow).toISOString(),
  ];
};

export const isStacItemLookupWindowValidForTime = (
  [startDate, endDate]: STACItemLookupWindow,
  timeValue: string | undefined,
  requiredMarginMs: number,
): boolean => {
  if (!startDate || !endDate || !timeValue) {
    return false;
  }

  const startTime = new Date(startDate).getTime();
  const endTime = new Date(endDate).getTime();
  const targetTime = new Date(timeValue).getTime();

  if (
    !Number.isFinite(startTime) ||
    !Number.isFinite(endTime) ||
    !Number.isFinite(targetTime)
  ) {
    return false;
  }

  return (
    targetTime - requiredMarginMs >= startTime &&
    targetTime + requiredMarginMs <= endTime
  );
};

const getSortedUniqueTimes = (timeValues: number[]): number[] =>
  Array.from(
    new Set(timeValues.filter((timeValue) => Number.isFinite(timeValue))),
  ).sort((firstTime, secondTime) => firstTime - secondTime);

const getClosestTimeIndex = (
  timeValues: { time: number }[],
  targetTime: number,
): number => {
  if (timeValues.length === 0) {
    throw new Error('getClosestTimeIndex requires at least one time value');
  }

  return timeValues.reduce((closestIndex, item, index) => {
    const closestDiff = Math.abs(timeValues[closestIndex].time - targetTime);
    const diff = Math.abs(item.time - targetTime);
    return diff < closestDiff ? index : closestIndex;
  }, 0);
};

export const getFrameStepInMilliseconds = (
  timeValue: string | undefined,
  prefetchList: number[],
  fallbackStepMs: number | undefined,
): number | undefined => {
  const targetTime = timeValue ? new Date(timeValue).getTime() : NaN;
  const prefetchTimes = getSortedUniqueTimes(prefetchList);

  if (Number.isFinite(targetTime) && prefetchTimes.length > 1) {
    const closestIndex = getClosestTimeIndex(
      prefetchTimes.map((time) => ({ time })),
      targetTime,
    );
    const frameSteps = [
      prefetchTimes[closestIndex] - prefetchTimes[closestIndex - 1],
      prefetchTimes[closestIndex + 1] - prefetchTimes[closestIndex],
    ].filter((step) => Number.isFinite(step) && step > 0);

    if (frameSteps.length > 0) {
      return Math.min(...frameSteps);
    }
  }

  return fallbackStepMs;
};

export const getLookupStepInMilliseconds = (
  frameStepMs: number | undefined,
  timeIntervalMs: number | undefined,
): number | undefined => {
  const candidateSteps = [frameStepMs, timeIntervalMs].filter(
    (step): step is number => step !== undefined && step > 0,
  );

  return candidateSteps.length > 0 ? Math.max(...candidateSteps) : undefined;
};

export const shouldPreloadStacFrames = (
  frameStepMs: number | undefined,
  timeIntervalMs: number | undefined,
): boolean =>
  frameStepMs === undefined ||
  timeIntervalMs === undefined ||
  frameStepMs >= timeIntervalMs;

export const getStacFrameTargetTimes = (
  timeValue: string | undefined,
  prefetchList: number[],
  fallbackStepMs: number | undefined,
  preloadRadius = STAC_GEOTIFF_FRAME_PRELOAD_RADIUS,
): number[] => {
  const targetTime = timeValue ? new Date(timeValue).getTime() : NaN;

  if (!Number.isFinite(targetTime)) {
    return [];
  }

  if (preloadRadius <= 0) {
    return [targetTime];
  }

  const prefetchTimes = getSortedUniqueTimes(prefetchList);

  if (prefetchTimes.length > 0) {
    const closestIndex = getClosestTimeIndex(
      prefetchTimes.map((time) => ({ time })),
      targetTime,
    );
    const startIndex = Math.max(0, closestIndex - preloadRadius);
    const endIndex = Math.min(
      prefetchTimes.length - 1,
      closestIndex + preloadRadius,
    );

    return getSortedUniqueTimes([
      ...prefetchTimes.slice(startIndex, endIndex + 1),
      targetTime,
    ]);
  }

  if (fallbackStepMs && fallbackStepMs > 0) {
    return getSortedUniqueTimes(
      Array.from(
        { length: preloadRadius * 2 + 1 },
        (_, index) => targetTime + (index - preloadRadius) * fallbackStepMs,
      ),
    );
  }

  return [targetTime];
};

export const getRequiredLookupMarginInMilliseconds = (
  timeValue: string | undefined,
  frameTargetTimes: number[],
  fallbackMarginMs: number,
): number => {
  const targetTime = timeValue ? new Date(timeValue).getTime() : NaN;

  if (!Number.isFinite(targetTime) || frameTargetTimes.length === 0) {
    return fallbackMarginMs;
  }

  const requiredMarginMs = Math.max(
    ...frameTargetTimes.map((frameTargetTime) =>
      Math.abs(frameTargetTime - targetTime),
    ),
  );

  return requiredMarginMs > 0 ? requiredMarginMs : fallbackMarginMs;
};

export const getStacFrameItemsToRender = (
  items: STACItem[] | undefined,
  timeValue?: string,
  frameTargetTimes?: number[],
  selectionBbox?: number[],
  frameTimeToleranceMs?: number,
  selectionTimeToleranceMs?: number,
): STACFrameItem[] => {
  if (!items || !timeValue) {
    return [];
  }

  const target = new Date(timeValue).getTime();
  if (!Number.isFinite(target)) {
    return [];
  }

  const itemsByTime = items
    .reduce<STACItemWithTime[]>((validItems, item, index) => {
      const { datetime } = item.properties;

      if (!datetime) {
        return validItems;
      }

      const time = new Date(datetime).getTime();

      if (Number.isFinite(time)) {
        validItems.push({ item, index, time });
      }

      return validItems;
    }, [])
    .sort(
      (firstItem, secondItem) =>
        firstItem.time - secondItem.time || firstItem.index - secondItem.index,
    );

  if (itemsByTime.length === 0) {
    return [];
  }

  const frames = itemsByTime.map((itemWithTime) => ({
    items: [itemWithTime],
    time: itemWithTime.time,
  }));

  const selectionCenter =
    selectionBbox?.length === 4
      ? [
          (selectionBbox[0] + selectionBbox[2]) / 2,
          (selectionBbox[1] + selectionBbox[3]) / 2,
        ]
      : undefined;
  const viewportFrameIndexes = selectionCenter
    ? frames.flatMap((frame, frameIndex) =>
        (selectionTimeToleranceMs === undefined ||
          Math.abs(frame.time - target) <= selectionTimeToleranceMs) &&
        frame.items.some(({ item }) => {
          const { bbox } = item;
          return (
            bbox?.length === 4 &&
            bbox[0] <= selectionCenter[0] &&
            bbox[1] <= selectionCenter[1] &&
            bbox[2] >= selectionCenter[0] &&
            bbox[3] >= selectionCenter[1]
          );
        })
          ? [frameIndex]
          : [],
      )
    : [];
  const closestViewportFrameIndex =
    viewportFrameIndexes.length > 0
      ? viewportFrameIndexes[
          getClosestTimeIndex(
            viewportFrameIndexes.map((frameIndex) => frames[frameIndex]),
            target,
          )
        ]
      : -1;

  let selectedFrameIndex = getClosestTimeIndex(frames, target);
  if (closestViewportFrameIndex >= 0) {
    selectedFrameIndex = closestViewportFrameIndex;
  }
  if (
    selectionTimeToleranceMs !== undefined &&
    Math.abs(frames[selectedFrameIndex].time - target) >
      selectionTimeToleranceMs
  ) {
    return [];
  }
  const targetTimes =
    frameTargetTimes && frameTargetTimes.length > 0
      ? frameTargetTimes
      : [target];
  const nearbyFrameIndexes =
    frameTimeToleranceMs !== undefined
      ? frames
          .map((_frame, frameIndex) => frameIndex)
          .filter(
            (frameIndex) =>
              Math.abs(frames[frameIndex].time - target) <=
              frameTimeToleranceMs,
          )
      : [];
  const frameIndexesToRender = Array.from(
    new Set(
      frameTimeToleranceMs !== undefined
        ? nearbyFrameIndexes
        : [
            selectedFrameIndex,
            ...targetTimes.map((targetTime) =>
              getClosestTimeIndex(frames, targetTime),
            ),
          ],
    ),
  ).sort(
    (firstIndex, secondIndex) =>
      frames[firstIndex].time - frames[secondIndex].time,
  );

  return frameIndexesToRender.flatMap((frameIndex) =>
    frames[frameIndex].items.map(({ item }) => ({
      item,
      isSelected: frameIndex === selectedFrameIndex,
    })),
  );
};
